import type {
  ConversationId,
  ConversationTimelineEventId,
  ShopperAiTagGroupId,
  ShopperAiTagId,
} from "@redotech/effect-api-shared/ids/definitions";
import { ConversationId as ConversationIdSchema } from "@redotech/effect-api-shared/ids/definitions";
import type {
  ConversationListCursor,
  ConversationListItemResponse,
  UpdateConversationInput,
} from "@redotech/effect-api-shared/models/shopper-ai/conversation/conversation";
import type { MessageWithConversationId } from "@redotech/effect-api-shared/models/shopper-ai/conversation/message";
import type { ConversationThread } from "@redotech/effect-api-shared/models/shopper-ai/conversation/thread";
import type { ConversationTimelineEventResponse } from "@redotech/effect-api-shared/models/shopper-ai/conversation/timeline-event/timeline-event-response";
import type {
  TagGroupResponse,
  TagResponse,
} from "@redotech/effect-api-shared/rpc/tag";
import type { RedoMerchantRpcClient } from "@redotech/merchant-sdk/rpc/client";
import type { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { getTeamReturns } from "@redotech/redo-merchant-app-common/client/team";
import type { EffectApiContext } from "@redotech/redo-merchant-app-common/effect-api-client-provider";
import { FulfillmentGroupPickStatus } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { Order } from "@redotech/redo-model/order";
import type { MerchantAppReturn } from "@redotech/redo-model/return";
import type { FilterGroup } from "@redotech/redo-model/views/views";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import {
  type SimpleComparison,
  parseLoadSubsetOptions,
  parseWhereExpression,
} from "@tanstack/db";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { tanstackFiltersToFilterGroup } from "../filters/conversation-tanstack-expressions";
import { catchApiErrors } from "./error-handling";

function toOptionIfPresent<T>(
  value: T | null | undefined,
): Option.Option<T> | undefined {
  if (value === undefined) {
    return undefined;
  }
  return value === null ? Option.none() : Option.some(value);
}

function toUpdateConversationInput(
  changes: Partial<ConversationListItemResponse>,
): UpdateConversationInput {
  return {
    status: changes.status,
    priority: toOptionIfPresent(changes.priority),
    readAt: toOptionIfPresent(changes.readAt),
    mongoAssigneeId: toOptionIfPresent(changes.mongoAssigneeId),
    tagUpdate: changes.tagIds
      ? { _tag: "replace", tagIds: changes.tagIds }
      : undefined,
  };
}

export const conversationQueryKeys: {
  readonly all: readonly ["conversations"];
  readonly list: () => readonly ["conversations", "list"];
  readonly threads: () => readonly ["conversations", "threads"];
  readonly messages: () => readonly ["conversations", "messages"];
  readonly timelineEvents: () => readonly ["conversations", "timelineEvents"];
} = {
  all: ["conversations"] as const,
  list: () => [...conversationQueryKeys.all, "list"] as const,
  threads: () => [...conversationQueryKeys.all, "threads"] as const,
  messages: () => [...conversationQueryKeys.all, "messages"] as const,
  timelineEvents: () =>
    [...conversationQueryKeys.all, "timelineEvents"] as const,
};

export const tagQueryKeys: { readonly all: readonly ["tags"] } = {
  all: ["tags"] as const,
};

export const orderQueryKeys: {
  readonly all: readonly ["orders"];
  readonly byEmail: (email: string) => readonly ["orders", "byEmail", string];
} = {
  all: ["orders"] as const,
  byEmail: (email: string) =>
    [...orderQueryKeys.all, "byEmail", email] as const,
};

export const returnQueryKeys: {
  readonly all: readonly ["returns"];
  readonly byEmail: (email: string) => readonly ["returns", "byEmail", string];
} = {
  all: ["returns"] as const,
  byEmail: (email: string) =>
    [...returnQueryKeys.all, "byEmail", email] as const,
};

interface CreateGlobalCollectionsOptions {
  queryClient: QueryClient;
  effectApi: EffectApiContext;
  rpcClient: RedoMerchantRpcClient;
  merchantClient: RedoMerchantClient;
}

const FIVE_MINUTES_MS = 5 * 60 * 1000;
const DEFAULT_PAGE_SIZE = 25;

export interface ConversationFetchResult {
  items: ConversationListItemResponse[];
  nextCursor: ConversationListCursor | null;
}

/**
 * Parses a TanStack DB where expression into SimpleComparison objects,
 * handling operators like `or` and `ilike` that extractSimpleComparisons
 * does not support.
 */
function parseWhereToSimpleComparisons(
  where: Parameters<typeof parseWhereExpression>[0],
): SimpleComparison[] {
  const result = parseWhereExpression<SimpleComparison | SimpleComparison[]>(
    where,
    {
      handlers: {
        and: (...args: (SimpleComparison | SimpleComparison[])[]) =>
          args.flat(),
        or: (...args: (SimpleComparison | SimpleComparison[])[]) => args.flat(),
        not: (inner: SimpleComparison | SimpleComparison[]) => {
          const items = Array.isArray(inner) ? inner : [inner];
          return items.map((c) => ({ ...c, operator: `not_${c.operator}` }));
        },
        eq: (field: string[], value: unknown) => ({
          field,
          operator: "eq",
          value,
        }),
        gt: (field: string[], value: unknown) => ({
          field,
          operator: "gt",
          value,
        }),
        gte: (field: string[], value: unknown) => ({
          field,
          operator: "gte",
          value,
        }),
        lt: (field: string[], value: unknown) => ({
          field,
          operator: "lt",
          value,
        }),
        lte: (field: string[], value: unknown) => ({
          field,
          operator: "lte",
          value,
        }),
        in: (first: unknown, second: unknown) => {
          // inArray(value, ref) → first=value, second=fieldPath
          // inArray(ref, values) → first=fieldPath, second=value
          if (Array.isArray(first)) {
            return { field: first, operator: "in", value: second };
          }
          return { field: second as string[], operator: "in", value: [first] };
        },
        ilike: (field: string[], value: unknown) => ({
          field,
          operator: "ilike",
          value,
        }),
        like: (field: string[], value: unknown) => ({
          field,
          operator: "like",
          value,
        }),
        isNull: (field: string[]) => ({ field, operator: "isNull" }),
        isUndefined: (field: string[]) => ({ field, operator: "isUndefined" }),
      },
    },
  );
  if (!result) {
    return [];
  }
  return Array.isArray(result) ? result : [result];
}

async function fetchSingleConversation(
  effectApi: EffectApiContext,
  conversationId: ConversationId,
): Promise<ConversationListItemResponse> {
  const client = effectApi.getClient();
  return effectApi.runPromise(
    catchApiErrors(
      client.conversation.getConversation({
        path: { id: ConversationIdSchema.make(conversationId) },
      }),
    ),
  );
}

function createConversationsListCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<ConversationListItemResponse, ConversationId>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.list(),
      queryFn: async (ctx): Promise<ConversationListItemResponse[]> => {
        //TODO: implement sorting
        const { where, limit } = ctx.meta?.loadSubsetOptions ?? {};
        const filters = parseWhereToSimpleComparisons(where);

        // When queried by a single conversation ID (e.g. direct navigation to
        // /support-v2/:conversationId), fetch that conversation directly
        // instead of going through the filtered list endpoint.
        const idFilter = filters.find((f) => f.field[0] === "id");
        if (idFilter?.operator === "eq" && typeof idFilter.value === "string") {
          const conversationId = ConversationIdSchema.make(idFilter.value);
          const conversation = await fetchSingleConversation(
            effectApi,
            conversationId,
          );
          return [conversation];
        }

        const requestedLimit = limit ?? 1;

        // Convert TanStack filters back to FilterGroup for the backend
        const filterGroup = tanstackFiltersToFilterGroup(filters);
        const encodedFilterGroup =
          filterGroup.clauses.length > 0
            ? encodeAdvancedFilterGroup(filterGroup)
            : undefined;

        const client = effectApi.getClient();
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getFilteredConversations({
              urlParams: {
                limit: requestedLimit,
                filterGroup: encodedFilterGroup,
              },
            }),
          ),
        );

        return [...result.items];
      },
      getKey: (item) => item.id,
      queryClient,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
      onUpdate: async ({ transaction }) => {
        const client = effectApi.getClient();

        for (const mutation of transaction.mutations) {
          await effectApi.runPromise(
            catchApiErrors(
              client.conversation.updateConversation({
                path: { id: ConversationIdSchema.make(mutation.key) },
                payload: toUpdateConversationInput(mutation.changes),
              }),
            ),
          );
        }
      },
    }),
  );
}

/**
 * Fetches conversations with cursor-based pagination and optional filter group.
 * This is used alongside the collection for infinite scroll support.
 */
export async function fetchConversationsPage(
  effectApi: EffectApiContext,
  options: {
    limit?: number;
    cursor?: ConversationListCursor;
    filterGroup?: FilterGroup;
  },
): Promise<ConversationFetchResult> {
  const encodedFilterGroup = options.filterGroup
    ? encodeAdvancedFilterGroup(options.filterGroup)
    : undefined;

  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(
      client.conversation.getFilteredConversations({
        urlParams: {
          limit: options.limit ?? DEFAULT_PAGE_SIZE,
          cursor: options.cursor,
          filterGroup: encodedFilterGroup,
        },
      }),
    ),
  );

  return { items: [...result.items], nextCursor: result.nextCursor };
}

function createThreadsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<ConversationThread, string>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.threads(),
      queryFn: async (ctx): Promise<ConversationThread[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          throw new Error(
            "Threads collection requires a single conversationId filter",
          );
        }

        const client = effectApi.getClient();
        const conversationId = ConversationIdSchema.make(
          conversationIdFilter.value,
        );
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getConversationThreads({
              path: { id: conversationId },
              urlParams: { limit: DEFAULT_PAGE_SIZE },
            }),
          ),
        );
        return [...result.items];
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
    }),
  );
}

function createMessagesCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<MessageWithConversationId, string>(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.messages(),
      queryFn: async (ctx): Promise<MessageWithConversationId[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          throw new Error(
            "Messages collection requires a single conversationId filter",
          );
        }

        const client = effectApi.getClient();
        const conversationId = ConversationIdSchema.make(
          conversationIdFilter.value,
        );
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getConversationMessages({
              path: { id: conversationId },
              urlParams: { limit: DEFAULT_PAGE_SIZE },
            }),
          ),
        );
        return result.items.map((message) => ({ ...message, conversationId }));
      },
      queryClient,
      getKey: (item) => item.id.toString(),
      syncMode: "on-demand",
    }),
  );
}

function createTimelineEventsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<
    ConversationTimelineEventResponse,
    ConversationTimelineEventId
  >(
    queryCollectionOptions({
      queryKey: conversationQueryKeys.timelineEvents(),
      queryFn: async (ctx): Promise<ConversationTimelineEventResponse[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const conversationIdFilter = filters.find(
          (f) => f.field[0] === "conversationId",
        );

        if (
          conversationIdFilter?.operator !== "eq" ||
          typeof conversationIdFilter.value !== "string"
        ) {
          throw new Error(
            "Timeline events collection requires a single conversationId filter",
          );
        }

        const client = effectApi.getClient();
        const conversationId = ConversationIdSchema.make(
          conversationIdFilter.value,
        );
        const result = await effectApi.runPromise(
          catchApiErrors(
            client.conversation.getConversationTimelineEvents({
              path: { id: conversationId },
            }),
          ),
        );
        return [...result.items];
      },
      queryClient,
      getKey: (item) => item.id,
      syncMode: "on-demand",
    }),
  );
}

interface AccountTagsResponse {
  tags: TagResponse[];
  tagGroups: TagGroupResponse[];
}

async function fetchAccountTags(
  effectApi: EffectApiContext,
): Promise<AccountTagsResponse> {
  const client = effectApi.getClient();
  const result = await effectApi.runPromise(
    catchApiErrors(client.tag.getAccountTags({})),
  );
  return { tags: [...result.tags], tagGroups: [...result.tagGroups] };
}

function createTagsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<TagResponse, ShopperAiTagId>(
    queryCollectionOptions({
      queryKey: tagQueryKeys.all,
      queryFn: async () => fetchAccountTags(effectApi),
      select: (data: AccountTagsResponse) => data.tags,
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

function createTagGroupsCollection({
  queryClient,
  effectApi,
}: CreateGlobalCollectionsOptions) {
  return createCollection<TagGroupResponse, ShopperAiTagGroupId>(
    queryCollectionOptions({
      queryKey: tagQueryKeys.all,
      queryFn: async () => fetchAccountTags(effectApi),
      select: (data: AccountTagsResponse) => data.tagGroups,
      getKey: (item) => item.id,
      queryClient,
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

export type OrderWithPickStatus = Order & {
  pickStatuses: FulfillmentGroupPickStatus[];
};

function createOrdersCollection({
  queryClient,
  rpcClient,
}: CreateGlobalCollectionsOptions) {
  return createCollection<OrderWithPickStatus, string>(
    queryCollectionOptions({
      queryKey: orderQueryKeys.all,
      queryFn: async (ctx): Promise<OrderWithPickStatus[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const emailFilter = filters.find((f) => f.field[0] === "shopify");

        if (
          emailFilter?.operator !== "eq" ||
          typeof emailFilter.value !== "string"
        ) {
          return [];
        }

        const result = await rpcClient.getTeamOrdersByEmail({
          email: emailFilter.value,
        });

        return result.orders as OrderWithPickStatus[];
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

async function fetchReturnsByEmail(
  merchantClient: RedoMerchantClient,
  email: string,
): Promise<MerchantAppReturn[]> {
  const result = await getTeamReturns(merchantClient, { email });
  return result as MerchantAppReturn[];
}

function createReturnsCollection({
  queryClient,
  merchantClient,
}: CreateGlobalCollectionsOptions) {
  return createCollection<MerchantAppReturn, string>(
    queryCollectionOptions({
      queryKey: returnQueryKeys.all,
      queryFn: async (ctx): Promise<MerchantAppReturn[]> => {
        const { filters } = parseLoadSubsetOptions(ctx.meta?.loadSubsetOptions);

        const emailFilter = filters.find(
          (f) => f.field[0] === "shipping_address",
        );

        if (
          emailFilter?.operator !== "eq" ||
          typeof emailFilter.value !== "string"
        ) {
          return [];
        }

        return fetchReturnsByEmail(merchantClient, emailFilter.value);
      },
      queryClient,
      getKey: (item) => item._id,
      syncMode: "on-demand",
      staleTime: FIVE_MINUTES_MS,
    }),
  );
}

export type ConversationsListCollection = ReturnType<
  typeof createConversationsListCollection
>;
export type ThreadsCollection = ReturnType<typeof createThreadsCollection>;
export type MessagesCollection = ReturnType<typeof createMessagesCollection>;
export type TagsCollection = ReturnType<typeof createTagsCollection>;
export type TagGroupsCollection = ReturnType<typeof createTagGroupsCollection>;
export type OrdersCollection = ReturnType<typeof createOrdersCollection>;
export type ReturnsCollection = ReturnType<typeof createReturnsCollection>;
export type TimelineEventsCollection = ReturnType<
  typeof createTimelineEventsCollection
>;

export interface GlobalConversationCollections {
  conversationsList: ConversationsListCollection;
  threads: ThreadsCollection;
  messages: MessagesCollection;
  tags: TagsCollection;
  tagGroups: TagGroupsCollection;
  orders: OrdersCollection;
  returns: ReturnsCollection;
  timelineEvents: TimelineEventsCollection;
}

export function createGlobalConversationCollections(
  options: CreateGlobalCollectionsOptions,
): GlobalConversationCollections {
  const conversationsList = createConversationsListCollection(options);
  const threads = createThreadsCollection(options);
  const messages = createMessagesCollection(options);
  const tags = createTagsCollection(options);
  const tagGroups = createTagGroupsCollection(options);
  const orders = createOrdersCollection(options);
  const returns = createReturnsCollection(options);
  const timelineEvents = createTimelineEventsCollection(options);

  return {
    conversationsList,
    threads,
    messages,
    tags,
    tagGroups,
    orders,
    returns,
    timelineEvents,
  };
}
